/* eslint-disable react-hooks/rules-of-hooks */
"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Input } from "@/Components/ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import { WorkPackage } from "./Plan"
import { useContext, useEffect, useState } from 'react'
import { AppContext } from "@/App"
import { PlanBuilderContext } from './Plan'
import DatePicker from "../ui/DatePicker"
import { Layers, NotebookTabs } from "lucide-react"
import { dateformat, isWorkingDay } from "@/utils/GlobalConstants"
import { format } from "date-fns"

const formSchema = z.object({
    name: z.string()
        .min(1, {
            message: "Work Package name is required",
        })
        .max(50, {
            message: "Work Package name must be less than 50 characters",
        }),
    startDate: z.coerce.date().refine((data) => isWorkingDay(data), {
        message: "Start date must be a working day",
    }),
    endDate: z.date(),
    ownerId: z.string()
})

interface WorkPackageFormProps {
    workPackage: WorkPackage
}

const frmWorkPackage = ({ workPackage }: WorkPackageFormProps) => {
    const app = useContext(AppContext);
    const props = useContext(PlanBuilderContext);
    const [allowSubmit, setAllowSubmit] = useState(true);
    const startDateDisabled = workPackage.tasks && workPackage.tasks.some(t => t.completed);

    const { reset, ...form } = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: workPackage.name,
            startDate: new Date(workPackage.startDate),
            endDate: new Date(workPackage.finishDate),
            ownerId: workPackage.ownerId ?? "unassigned"
        },
        mode: "onChange",
    });

    useEffect(() => {
        reset({
            name: workPackage.name,
            startDate: new Date(workPackage.startDate),
            endDate: new Date(workPackage.finishDate),
            ownerId: workPackage.ownerId ?? "unassigned"
        });
        setTimeout(() => form.setFocus("name"), 0);
    }, [workPackage, reset]);

    useEffect(() => {
        // Debounce the effect to prevent multiple calls when the selected Task changes
        const timeout = setTimeout(() => {
            const startDate = new Date(form.getValues("startDate"));
            const projectStartDate = new Date(props.projectStartDate);
            startDate.setHours(0, 0, 0, 0);
            projectStartDate.setHours(0, 0, 0, 0);
            if (startDate < projectStartDate) {
                form.setError("startDate", {
                    type: "manual",
                    message: `Start date must be after ${format(projectStartDate, dateformat)}`,
                });
                setAllowSubmit(false);
            } else {
                form.clearErrors("startDate");
                setAllowSubmit(true);
            }
        }, 300); // Adjust debounce delay as needed

        return () => clearTimeout(timeout); // Cleanup timeout on dependency change
    }, [form, props.projectStartDate]);

    function onSubmit(data: z.infer<typeof formSchema>) {
        const updatedWorkPackage: WorkPackage = {
            ...workPackage,
            name: data.name,
            startDate: new Date(data.startDate),
            ownerId: data.ownerId == "unassigned" ? "" : data.ownerId,
        };
        props.updateWorkPackage(updatedWorkPackage)
    }

    return (
        <Form {...form} reset={reset}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Name" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Start Date</FormLabel>
                            <FormControl>
                                <div className="relative group">
                                    <DatePicker date={new Date(field.value)} setDate={field.onChange} disable={startDateDisabled} />
                                    {startDateDisabled && (
                                        <div className="absolute left-8 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-muted text-xs text-foreground px-2 py-1 rounded shadow-lg">
                                            This work package contains completed tasks, so the start date cannot be changed here.
                                        </div>
                                    )}
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {/* <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>End Date</FormLabel>
                            <FormControl>
                                <DatePicker date={new Date(field.value)} setDate={field.onChange}/>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                /> */}
                <FormField
                    control={form.control}
                    name="ownerId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Owner</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Assign to Team Member" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                    {app.teamMembers?.map((t) => (
                                        <SelectItem key={t.id} value={t.id as string}>{t.email}</SelectItem>
                                    ))}
                                    {app.projectManagers?.map((m) => (
                                        <SelectItem key={m.id} value={m.id as string}>{m.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className="flex flex-row space-x-4 w-full">
                    <button type="submit" disabled={!allowSubmit} className="flex flex-row items-center button-default w-full mr-2"><Layers className="w-6 h-6 mr-2" />Update Work Package</button><button onClick={() => props.addTask(workPackage.id)} className="flex flex-row items-center button-default w-min text-nowrap"><NotebookTabs className="w-6 h-6 mr-2" />Add Task</button>
                </div>
            </form>
        </Form>
    )
}

export default frmWorkPackage